import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type ChitChatStatus = {
  id: string;
  user_id: string;
  media_type: "text" | "image" | "video";
  content: string | null;
  media_url: string | null;
  bg_color: string | null;
  privacy: "all" | "contacts" | "except" | "only";
  created_at: string;
  expires_at: string;
};

export function useChitChatStatus() {
  const { user } = useAuth();
  const [statuses, setStatuses] = useState<ChitChatStatus[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const nowIso = new Date().toISOString();
    const { data } = await supabase
      .from("chitchat_statuses")
      .select("*")
      .gt("expires_at", nowIso)
      .order("created_at", { ascending: false });
    setStatuses((data as ChitChatStatus[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const ch = supabase
      .channel("chitchat-status-feed")
      .on("postgres_changes", { event: "*", schema: "public", table: "chitchat_statuses" }, () => load())
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [load]);

  const createTextStatus = useCallback(
    async (
      content: string,
      bg: string,
      privacy: ChitChatStatus["privacy"] = "contacts",
      audienceUserIds: string[] = [],
    ) => {
      if (!user) return;
      const { data, error } = await supabase
        .from("chitchat_statuses")
        .insert({
          user_id: user.id,
          media_type: "text",
          content,
          bg_color: bg,
          privacy,
        })
        .select()
        .single();
      if (error) return { error };
      // Persist audience for "only" / "except" privacy modes
      if (data && (privacy === "only" || privacy === "except") && audienceUserIds.length) {
        const mode = privacy === "only" ? "include" : "exclude";
        await supabase.from("chitchat_status_audience").insert(
          audienceUserIds.map((uid) => ({ status_id: data.id, user_id: uid, mode })),
        );
      }
      load();
      return { error: null };
    },
    [user, load],
  );

  const recordView = useCallback(
    async (statusId: string) => {
      if (!user) return;
      await supabase
        .from("chitchat_status_views")
        .insert({ status_id: statusId, viewer_id: user.id })
        .select();
    },
    [user],
  );

  const deleteStatus = useCallback(
    async (id: string) => {
      const { error } = await supabase.from("chitchat_statuses").delete().eq("id", id);
      if (!error) load();
      return { error };
    },
    [load],
  );

  return { statuses, loading, createTextStatus, recordView, deleteStatus, reload: load };
}
